import { useEffect, useMemo, useRef, useState } from "react";
import LeagueAnalysisEntry from "./LeagueAnalysisEntry";
import { Dialog, DialogContent, ThemeProvider } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FEATURES, hasFeatureAccess } from "../helpers/featureAccess";
import { workareaTheme } from "../helpers/workareaTheme";
import { compareLeagueSelection } from "../helpers/leagueSelection";
import LeagueChoice from "./LeagueChoice";
import { leagueAnalysisRequest } from "../helpers/leagueAnalysisRequest";
import { tipsRequest } from "../helpers/tipsWorkflow";
import {
  detailFields,
  previewDetailDraft,
} from "../helpers/leagueAnalysisDraft";
import {
  adjustGoalAverage,
  adjustHomeAdvantage,
} from "../helpers/adjustTeamStrengths";
import { sortAndRankTeams } from "../helpers/sortAndRankTeams";
import { CompactLeagueTable } from "./CompactLeagueTable";
import TeamContext from "./TeamContext";
import { fetchFixtureOddsRecords } from "./OddsTooltip";
import {
  aggregateFixtureMarketOdds,
  readAggregatedMarketOdd,
} from "../helpers/fixtureMarketOdds";
import {
  fixtureTeams,
  primaryLeagueTips,
  ADMIN_TIP_PROFILE,
  leagueTipTitle,
} from "../helpers/adminLeague";
import { sortFixturesCanonical } from "../helpers/fixtureOrder";
import { isLeagueFixtureFinished } from "../helpers/leagueWorkspaceState";
import { formatFairOdd } from "../helpers/leagueFairMarkets";
import "./AllInOne.css";
const targetsFor = (rows) =>
  Object.fromEntries(
    rows.map((r) => [
      r.team_id,
      Object.fromEntries(detailFields.map((f) => [f, r[f]])),
    ])
  );
const newId = () =>
  window.crypto?.randomUUID?.() ||
  `workspace-${Date.now()}-${Math.random().toString(36).slice(2)}`;
export default function AllInOne(props) {
  if (!hasFeatureAccess(FEATURES.ALL_IN_ONE, props.user)) return null;
  return (
    <ThemeProvider theme={workareaTheme}>
      <Workspace {...props} />
    </ThemeProvider>
  );
}
function Workspace({ leagues = [] }) {
  const { i18n } = useTranslation(),
    en = i18n.language?.startsWith("en"),
    txt = (de, english) => (en ? english : de);
  const fmt = (n) =>
    typeof n === "number" && Number.isFinite(n)
      ? n.toLocaleString(en ? "en-GB" : "de-DE", { maximumFractionDigits: 6 })
      : "—";
  const [catalog, setCatalog] = useState([]),
    [context, setContext] = useState(null),
    [leagueId, setLeagueId] = useState("");
  const [targets, setTargets] = useState(null),
    [draw, setDraw] = useState(null),
    [preview, setPreview] = useState(null),
    [suggestions, setSuggestions] = useState(null);
  const [selected, setSelected] = useState([]),
    [fixtureId, setFixtureId] = useState(null),
    [filter, setFilter] = useState("all"),
    [tab, setTab] = useState("table"),
    [round, setRound] = useState("");
  const [details, setDetails] = useState({}),
    [quotes, setQuotes] = useState({}),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [more, setMore] = useState(false),
    [revisions, setRevisions] = useState([]);
  const [paramInputs, setParamInputs] = useState({}),
    [detailScope, setDetailScope] = useState("overall"),
    [needsReload, setNeedsReload] = useState(false);
  const [calibrationOpen, setCalibrationOpen] = useState(false);
  const calibrationSaved = useRef(false);
  const calibrationDirty = useRef(false);
  const closeCalibration = () => {
    if (
      calibrationDirty.current &&
      !window.confirm(
        txt(
          "Ungespeicherten Kalibrierungsentwurf verwerfen?",
          "Discard unsaved calibration draft?"
        )
      )
    )
      return;
    setCalibrationOpen(false);
    if (calibrationSaved.current) act(() => load(leagueId));
  };
  const run = useRef(newId()),
    sequence = useRef(0),
    mounted = useRef(true);
  const dirty =
    !!context &&
    (JSON.stringify(targets) !==
      JSON.stringify(targetsFor(context.teams.map((t) => t.setting))) ||
      draw !== context.parameters.drawfactor ||
      Object.entries(paramInputs).some(
        ([f, v]) => v !== String(context.parameters[f])
      ));
  const payload = {
    league_id: context?.league.id,
    season_id: context?.season.id,
    revision: context?.revision,
    team_targets: targets,
    drawfactor: draw,
  };
  const request = (mode, extra = {}) =>
    leagueAnalysisRequest({ mode: `workspace-${mode}`, ...payload, ...extra });
  async function act(fn) {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      if (mounted.current) setError(e.message);
    } finally {
      if (mounted.current) setBusy(false);
    }
  }
  useEffect(() => {
    mounted.current = true;
    act(async () => {
      const r = await leagueAnalysisRequest({ mode: "workspace-catalog" });
      if (mounted.current) setCatalog(r.leagues);
    });
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (!dirty) return;
    const unload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    const navigation = (e) => {
      if (
        !window.confirm(
          en
            ? "Discard unsaved changes?"
            : "Ungespeicherte Änderungen verwerfen?"
        )
      )
        e.preventDefault();
    };
    window.addEventListener("beforeunload", unload);
    window.addEventListener("betty:before-navigation", navigation);
    return () => {
      window.removeEventListener("beforeunload", unload);
      window.removeEventListener("betty:before-navigation", navigation);
    };
  }, [dirty, en]);
  const available = leagues
    .filter((l) => catalog.some((c) => Number(c.league_id) === Number(l.id)))
    .sort(compareLeagueSelection);
  function reset(c = context) {
    setTargets(targetsFor(c.teams.map((t) => t.setting)));
    setDraw(c.parameters.drawfactor);
    setParamInputs(
      Object.fromEntries(
        ["stored_goal_average", "exp_home_factor", "drawfactor"].map((f) => [
          f,
          String(c.parameters[f]),
        ])
      )
    );
    setNeedsReload(false);
    setPreview(null);
    setSuggestions(null);
    run.current = newId();
  }
  async function load(id) {
    const n = ++sequence.current;
    const entry = catalog.find((l) => Number(l.league_id) === Number(id));
    const season =
      entry?.seasons.find((s) => s.is_current) || entry?.seasons[0];
    if (!season) throw Error("SEASON_MISSING");
    const r = await leagueAnalysisRequest({
      mode: "workspace-context",
      league_id: Number(id),
      season_id: Number(season.id),
    });
    if (!mounted.current || n !== sequence.current) return;
    setContext(r.context);
    setLeagueId(id);
    reset(r.context);
    setSelected([]);
    setFilter("all");
    setFixtureId(null);
    setDetails({});
    setQuotes({});
    setRound("");
    setRevisions([]);
    const history = await leagueAnalysisRequest({
      mode: "workspace-revisions",
      league_id: Number(id),
      season_id: Number(season.id),
    });
    if (mounted.current && n === sequence.current)
      setRevisions(history.revisions || []);
  }
  let editRows = [],
    invalid = false;
  if (context && targets)
    try {
      editRows = previewDetailDraft(
        context.teams.map((t) => t.setting),
        targets,
        Number(draw),
        { normalize: false }
      );
    } catch {
      invalid = true;
    }
  const parameterInvalid =
    context &&
    Object.entries(paramInputs).some(
      ([f, v]) =>
        v === "" ||
        !Number.isFinite(Number(v)) ||
        Number(v) <= 0 ||
        (f === "exp_home_factor" && Number(v) >= 2)
    );
  const upcoming = useMemo(
    () =>
      sortFixturesCanonical(
        (context?.workspace?.fixtures || []).filter(
          (f) =>
            !isLeagueFixtureFinished(f) &&
            new Date(f.starting_at).getTime() >= Date.now() - 3600000
        )
      ),
    [context]
  );
  const rounds = [
    ...new Set(
      upcoming.map((f) => String(f.round_id ?? f.round?.id ?? "none"))
    ),
  ];
  const effectiveRound = round || rounds[0] || "";
  const fixtures = useMemo(
    () =>
      upcoming.filter(
        (f) => String(f.round_id ?? f.round?.id ?? "none") === effectiveRound
      ),
    [upcoming, effectiveRound]
  );
  useEffect(() => {
    let live = true;
    setDetails({});
    setQuotes({});
    if (!context || !fixtures.length) return;
    (async () => {
      try {
        const r = await tipsRequest("get-details-for-fixtures", {
          fixtures,
          season_id: context.season.id,
        });
        if (live)
          setDetails(
            Object.fromEntries(
              (Array.isArray(r) ? r : []).map((d) => [d.id, d])
            )
          );
        const records = await Promise.all(
          fixtures.map(async (f) => {
            try {
              return [f.id, await fetchFixtureOddsRecords(f.id)];
            } catch {
              return [f.id, null];
            }
          })
        );
        if (live) setQuotes(Object.fromEntries(records));
      } catch (e) {
        if (live) setError(e.message);
      }
    })();
    return () => {
      live = false;
    };
  }, [context, fixtures]);
  const fixture = context?.workspace?.fixtures?.find(
    (f) => Number(f.id) === Number(fixtureId)
  );
  const pair = fixture ? fixtureTeams(fixture).map((t) => Number(t.id)) : [];
  const visible =
    filter === "all" ? null : filter === "fixture" ? pair : selected;
  function selectTeam(id) {
    if (busy) return;
    setSelected([Number(id)]);
    setFixtureId(null);
    setPreview(null);
  }
  function edit(id, field, value) {
    setTargets((t) => ({
      ...t,
      [id]: { ...t[id], [field]: value === "" ? "" : Number(value) },
    }));
    setPreview(null);
    run.current = newId();
  }
  const tables = context?.workspace?.tables || {};
  const differences =
    preview?.normalized.rows.flatMap((r) =>
      detailFields
        .filter(
          (f) => Math.abs(r[f] - preview.edit.teamMap[r.team_id][f]) > 1e-12
        )
        .map((f) => ({
          id: r.team_id,
          field: f,
          before: preview.edit.teamMap[r.team_id][f],
          after: r[f],
        }))
    ) || [];
  const selectedTeams =
    context?.teams.filter((t) => selected.includes(t.team_id)) || [];
  function odds(detail) {
    return ["homewin", "draw", "awaywin"]
      .map((k) =>
        formatFairOdd(detail?.probs?.[k] > 0 ? 1 / detail.probs[k] : null)
      )
      .join(" / ");
  }
  function tipText(f, d) {
    if (!quotes[f.id]) return "—";
    return (
      primaryLeagueTips({ ...f, ...d }, quotes[f.id], ADMIN_TIP_PROFILE)
        .map((t) => leagueTipTitle({ ...t, fixture: f }))
        .join(" · ") || "—"
    );
  }
  return (
    <section className="aio" aria-label="All In One">
      <header>
        <h2>
          All In One{" "}
          <small>{txt("Admin · experimentell", "Admin · experimental")}</small>
        </h2>
        <LeagueChoice
          leagues={available}
          value={leagueId}
          disabled={busy || needsReload}
          label={txt("Liga", "League")}
          onChange={(id) => {
            if (
              !dirty ||
              window.confirm(txt("Änderungen verwerfen?", "Discard changes?"))
            )
              act(() => load(id));
          }}
        />
        {context && <strong>{context.season.name}</strong>}
        {busy && (
          <span role="status">
            {txt("Wird geladen / berechnet …", "Loading / calculating …")}
          </span>
        )}
      </header>
      {error && <p role="alert">{error}</p>}
      {notice && <p role="status">{notice}</p>}
      {needsReload && (
        <button
          disabled={busy || needsReload}
          onClick={() => act(() => load(leagueId))}
        >
          {txt("Gespeicherten Stand neu laden", "Reload saved state")}
        </button>
      )}
      {context && (
        <>
          <button
            disabled={busy || dirty || needsReload}
            onClick={() => {
              calibrationSaved.current = false;
              setCalibrationOpen(true);
            }}
          >
            {txt("Liga kalibrieren", "Calibrate league")}
          </button>
          {calibrationOpen && (
            <Dialog
              open
              fullWidth
              maxWidth="xl"
              onClose={closeCalibration}
              PaperProps={{
                sx: {
                  bgcolor: "#152936",
                  color: "#edf3f7",
                  maxHeight: "94dvh",
                },
              }}
            >
              <DialogContent>
                <button onClick={closeCalibration}>
                  {txt("Kalibrierung schließen", "Close calibration")}
                </button>
                <LeagueAnalysisEntry
                  leagues={leagues}
                  initialLeagueId={leagueId}
                  singleLeague
                  onDirtyChange={(dirty) => {
                    calibrationDirty.current = dirty;
                  }}
                  onSaved={() => {
                    calibrationSaved.current = true;
                    setNeedsReload(true);
                  }}
                />
              </DialogContent>
            </Dialog>
          )}
          <div className="aio-parameters">
            {["stored_goal_average", "exp_home_factor", "drawfactor"].map(
              (f, i) => (
                <label key={f}>
                  {
                    [
                      txt("Torschnitt", "Goal average"),
                      txt("Heimvorteil", "Home advantage"),
                      txt("Remisfaktor", "Draw factor"),
                    ][i]
                  }
                  <span>
                    {txt("Gespeichert", "Saved")}: {fmt(context.parameters[f])}{" "}
                    · Edit: {fmt(preview?.edit.parameters[f])} ·{" "}
                    {txt("Nach Normalisierung", "Normalized")}:{" "}
                    {fmt(preview?.normalized.parameters[f])}
                  </span>
                  <input
                    aria-label={f}
                    type="number"
                    step="0.01"
                    disabled={busy || needsReload}
                    value={paramInputs[f] ?? ""}
                    title={txt(
                      "Zielmittelwert im Edit-Zustand",
                      "Target mean in edit state"
                    )}
                    onChange={(e) => {
                      const raw = e.target.value,
                        value = Number(raw);
                      setParamInputs((p) => ({ ...p, [f]: raw }));
                      setPreview(null);
                      run.current = newId();
                      if (
                        raw === "" ||
                        !Number.isFinite(value) ||
                        value <= 0 ||
                        (f === "exp_home_factor" && value >= 2)
                      )
                        return;
                      try {
                        if (f === "drawfactor") setDraw(value);
                        else
                          setTargets(
                            targetsFor(
                              (f === "stored_goal_average"
                                ? adjustGoalAverage
                                : adjustHomeAdvantage)(editRows, value)
                            )
                          );
                      } catch (err) {
                        setError(err.message);
                      }
                    }}
                  />
                </label>
              )
            )}
          </div>
          <div className="aio-actions">
            <button disabled={busy || !dirty} onClick={() => reset()}>
              {txt("Änderungen verwerfen", "Discard edits")}
            </button>
            <button
              disabled={busy || invalid || parameterInvalid || needsReload}
              onClick={() =>
                act(async () =>
                  setPreview(
                    await request("preview", { fixture_id: fixtureId })
                  )
                )
              }
            >
              {txt("Preview / neu berechnen", "Preview / calculate")}
            </button>
            <button
              disabled={
                busy ||
                invalid ||
                !dirty ||
                !preview ||
                parameterInvalid ||
                needsReload ||
                !context.atomic_save_available
              }
              onClick={() =>
                act(async () => {
                  await request("save-draft", {
                    confirm: true,
                    run_id: run.current,
                  });
                  setNeedsReload(true);
                  setNotice(
                    txt(
                      "Normalisiert gespeichert; keine Fixtures neu kompiliert.",
                      "Saved normalized settings; no fixtures recompiled."
                    )
                  );
                  await load(leagueId);
                })
              }
            >
              {txt("Settings speichern", "Save settings")}
            </button>
          </div>
          <p className="aio-note">
            {txt(
              "Beim Speichern werden die Team Settings der Liga gemeinsam normalisiert. Dadurch können sich die eingegebenen Werte und weitere Teamwerte geringfügig verändern.",
              "Saving normalizes the league's team settings together. Entered values and other teams may change slightly."
            )}
          </p>
          {(invalid || parameterInvalid) && (
            <p role="alert">
              {txt(
                "Bitte gültige positive Werte eingeben.",
                "Enter valid positive values."
              )}
            </p>
          )}
          {preview && (
            <p
              className="aio-normalization"
              aria-label={txt(
                "Normalisierung der Eingaben",
                "Input normalization"
              )}
            >
              {differences
                .filter(
                  (d) =>
                    targets[d.id][d.field] !==
                    context.teams.find((t) => t.team_id === d.id)?.setting[
                      d.field
                    ]
                )
                .slice(0, 8)
                .map((d) => (
                  <span key={`${d.id}:${d.field}`}>
                    {context.teams.find((t) => t.team_id === d.id)?.name}{" "}
                    {d.field}: {fmt(d.before)} → {fmt(d.after)}.{" "}
                  </span>
                ))}
              {txt(
                "Alle weiteren Änderungen stehen in den Details.",
                "See details for all other changes."
              )}
            </p>
          )}
          {preview && (
            <details>
              <summary>
                {txt(
                  "Vor → nach Normalisierung",
                  "Before → after normalization"
                )}{" "}
                · {new Set(differences.map((d) => d.id)).size} Teams /{" "}
                {differences.length} {txt("Werte", "values")}
              </summary>
              <table>
                <thead>
                  <tr>
                    <th>Team</th>
                    <th>{txt("Wert", "Field")}</th>
                    <th>Edit</th>
                    <th>{txt("Nach Normalisierung", "Normalized")}</th>
                  </tr>
                </thead>
                <tbody>
                  {differences.map((d) => (
                    <tr key={`${d.id}:${d.field}`}>
                      <td>
                        {context.teams.find((t) => t.team_id === d.id)?.name}
                      </td>
                      <td>{d.field}</td>
                      <td>{fmt(d.before)}</td>
                      <td>{fmt(d.after)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
          <div className="aio-controls">
            <select
              aria-label={txt("Teamfilter", "Team filter")}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">{txt("Alle Teams", "All teams")}</option>
              <option value="team">
                {txt("Nur ausgewähltes Team", "Selected team")}
              </option>
              <option value="fixture">
                {txt("Teams des Fixtures", "Fixture teams")}
              </option>
            </select>
            <label>
              <input
                type="checkbox"
                checked={more}
                onChange={(e) => setMore(e.target.checked)}
              />
              {txt("Ableitungen", "Derived values")}
            </label>
          </div>
          <div className="aio-grid">
            <div className="aio-scroll">
              <h3>
                Team Settings ·{" "}
                {txt("Edit / unnormalisiert", "Edit / unnormalized")}
              </h3>
              <table className="aio-settings">
                <thead>
                  <tr>
                    {[
                      "#",
                      "Team",
                      "Strength",
                      "Goal Sum",
                      ...detailFields,
                      ...(more
                        ? [
                            "for_total",
                            "against_total",
                            "for_homefactor",
                            "against_homefactor",
                          ]
                        : []),
                    ].map((f) => (
                      <th key={f}>{f}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortAndRankTeams(
                    invalid ? context.teams.map((t) => t.setting) : editRows
                  )
                    .filter(
                      (r) => visible === null || visible.includes(r.team_id)
                    )
                    .map((r) => {
                      const t = context.teams.find(
                        (t) => t.team_id === r.team_id
                      );
                      return (
                        <tr
                          key={r.team_id}
                          data-selected={selected.includes(r.team_id)}
                        >
                          <td>{r.rank}</td>
                          <th>
                            <button onClick={() => selectTeam(r.team_id)}>
                              {t.logo && <img src={t.logo} alt="" />}
                              {t.name}
                            </button>
                          </th>
                          <td>
                            <b>{fmt(r.total_strength)}</b>
                          </td>
                          <td>
                            <b>{fmt(r.total_goals)}</b>
                          </td>
                          {detailFields.map((f) => {
                            const s = suggestions?.suggestions.find(
                              (s) => s.id === String(r.team_id) && s.field === f
                            );
                            const games =
                              t.references.actual?.[
                                f.endsWith("home") ? "home" : "away"
                              ]?.played;
                            const real =
                                t.references.actual?.fixtureSettingValues?.[f],
                              should =
                                t.references.should?.fixtureSettingValues?.[f];
                            const conflict =
                              Number.isFinite(real) &&
                              Number.isFinite(should) &&
                              (real - t.setting[f]) * (should - t.setting[f]) <
                                0;
                            const symbol =
                              !games || games < 5
                                ? "?"
                                : conflict
                                ? "!"
                                : s
                                ? Math.abs(s.proposed - t.setting[f]) < 0.001
                                  ? "="
                                  : s.proposed > t.setting[f]
                                  ? "↑"
                                  : "↓"
                                : "";
                            return (
                              <td key={f}>
                                <input
                                  aria-label={`${t.name} ${f}`}
                                  type="number"
                                  step="0.001"
                                  disabled={busy || needsReload}
                                  value={targets[r.team_id][f]}
                                  onChange={(e) =>
                                    edit(r.team_id, f, e.target.value)
                                  }
                                />
                                <small>
                                  {txt("Gespeichert", "Saved")}:{" "}
                                  {fmt(t.setting[f])}{" "}
                                  <span
                                    title={txt(
                                      "↑/↓ Vorschlag · = unverändert · ! Real/Should gegenläufig · ? weniger als 5 Spiele",
                                      "↑/↓ suggestion · = unchanged · ! Real/Should conflict · ? fewer than 5 games"
                                    )}
                                  >
                                    {symbol}
                                  </span>
                                </small>
                                {s && (
                                  <button
                                    disabled={busy || needsReload}
                                    title={txt(
                                      "Nur in Edit übernehmen",
                                      "Apply to edit only"
                                    )}
                                    onClick={() =>
                                      edit(r.team_id, f, s.proposed)
                                    }
                                  >
                                    → {fmt(s.proposed)}
                                  </button>
                                )}
                              </td>
                            );
                          })}
                          {more &&
                            [
                              "for_total",
                              "against_total",
                              "for_homefactor",
                              "against_homefactor",
                            ].map((f) => <td key={f}>{fmt(r[f])}</td>)}
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
            <div className="aio-side">
              <nav>
                <button
                  aria-pressed={tab === "table"}
                  onClick={() => setTab("table")}
                >
                  {txt("Tabelle / Statistik", "Table / statistics")}
                </button>
                <button
                  aria-pressed={tab === "fixtures"}
                  onClick={() => setTab("fixtures")}
                >
                  {txt(
                    "Nächste Spiele / Indikationen",
                    "Next matches / indications"
                  )}
                </button>
              </nav>
              {tab === "table" ? (
                <CompactLeagueTable
                  actualRows={tables.actual || []}
                  expectedRows={tables.expected || []}
                  shouldRows={tables.should || []}
                  teamLogos={
                    new Map(context.teams.map((t) => [t.team_id, t.logo]))
                  }
                  selectedTeamIds={selected}
                  visibleTeamIds={visible}
                  onSelectTeam={selectTeam}
                  seasonId={context.season.id}
                  teamSettings={Object.fromEntries(
                    editRows.map((r) => [r.team_id, r])
                  )}
                  displayTeamNames={
                    new Map(context.teams.map((t) => [t.team_id, t.name]))
                  }
                />
              ) : (
                <>
                  <label>
                    {txt("Spieltag", "Round")}{" "}
                    <select
                      disabled={busy}
                      value={effectiveRound}
                      onChange={(e) => {
                        setRound(e.target.value);
                        setFixtureId(null);
                        setSelected([]);
                        setPreview(null);
                      }}
                    >
                      {rounds.map((id) => (
                        <option key={id} value={id}>
                          {upcoming.find(
                            (f) =>
                              String(f.round_id ?? f.round?.id ?? "none") === id
                          )?.round?.name || id}
                        </option>
                      ))}
                    </select>
                  </label>
                  {!fixtures.length && (
                    <p>
                      {txt(
                        "Keine kommenden Fixtures verfügbar.",
                        "No upcoming fixtures available."
                      )}
                    </p>
                  )}
                  <table>
                    <thead>
                      <tr>
                        <th>{txt("Spiel", "Match")}</th>
                        <th>Fair 1/X/2</th>
                        <th>{txt("Markt Ø", "Market avg")}</th>
                        <th>{txt("Indikation", "Indication")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fixtures.map((f) => (
                        <tr
                          key={f.id}
                          data-selected={Number(fixtureId) === Number(f.id)}
                        >
                          <td>
                            <button
                              disabled={busy}
                              onClick={() => {
                                setFixtureId(f.id);
                                setSelected(
                                  fixtureTeams(f).map((t) => Number(t.id))
                                );
                                setPreview(null);
                              }}
                            >
                              {fixtureTeams(f)
                                .map((t) => t.name)
                                .join(" – ")}
                              <small>{f.starting_at}</small>
                            </button>
                          </td>
                          <td>{odds(details[f.id])}</td>
                          <td>
                            {["1", "X", "2"]
                              .map((o) =>
                                fmt(
                                  readAggregatedMarketOdd(
                                    aggregateFixtureMarketOdds(
                                      quotes[f.id] || [],
                                      "1x2"
                                    ),
                                    o,
                                    "average"
                                  )
                                )
                              )
                              .join(" / ")}
                          </td>
                          <td>{tipText(f, details[f.id])}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
          {fixture && (
            <section className="aio-pair">
              <h3>
                {fixtureTeams(fixture)
                  .map((t) => t.name)
                  .join(" – ")}
              </h3>
              <table>
                <thead>
                  <tr>
                    <th>{txt("Zustand", "State")}</th>
                    <th>xG H/A</th>
                    <th>
                      {txt("Wahrscheinlichkeit 1/X/2", "Probability 1/X/2")}
                    </th>
                    <th>Fair 1/X/2</th>
                    <th>{txt("Indikation", "Indication")}</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    [
                      txt("Gespeicherte Prognose", "Stored forecast"),
                      details[fixture.id],
                    ],
                    ["Edit / Preview", preview?.fixture?.detail],
                  ].map(([label, d]) => (
                    <tr key={label}>
                      <th>{label}</th>
                      <td>
                        {fmt(d?.goalexp?.home)} / {fmt(d?.goalexp?.away)}
                      </td>
                      <td>
                        {["homewin", "draw", "awaywin"]
                          .map((k) =>
                            d?.probs?.[k] != null
                              ? `${fmt(d.probs[k] * 100)} %`
                              : "—"
                          )
                          .join(" / ")}
                      </td>
                      <td>{odds(d)}</td>
                      <td>{d ? tipText(fixture, d) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p>
                Market xG:{" "}
                {fmt(
                  details[fixture.id]?.market_derived
                    ?.market_expected_home_goals
                )}{" "}
                /{" "}
                {fmt(
                  details[fixture.id]?.market_derived
                    ?.market_expected_away_goals
                )}{" "}
                · Fit Error:{" "}
                {fmt(details[fixture.id]?.market_derived?.market_fit_error)}
              </p>
            </section>
          )}
          <div className="aio-team-details">
            {selectedTeams.map((t) => (
              <section key={t.team_id}>
                <h3>{t.name}</h3>
                <nav>
                  {[
                    ["overall", txt("Gesamt", "Overall")],
                    ["home", txt("Heim", "Home")],
                    ["away", txt("Auswärts", "Away")],
                  ].map(([k, label]) => (
                    <button
                      key={k}
                      aria-pressed={detailScope === k}
                      onClick={() => setDetailScope(k)}
                    >
                      {label}
                    </button>
                  ))}
                </nav>
                <div className="aio-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>{txt("Quelle", "Source")}</th>
                        {detailFields.map((f) => (
                          <th key={f}>{f}</th>
                        ))}
                        <th>{txt("Spiele G/H/A", "Games T/H/A")}</th>
                        <th>W/D/L</th>
                        <th>{txt("Tore absolut", "Total goals")}</th>
                        <th>{txt("Tore pro Spiel", "Goals per game")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        [
                          txt("Gespeichert", "Saved"),
                          { fixtureSettingValues: t.setting },
                        ],
                        ...[
                          "actual",
                          "expected",
                          "should",
                          "market",
                          "previous_saved",
                          "previous",
                          "previous_expected",
                          "previous_should",
                        ].map((k) => [k, t.references[k]]),
                      ].map(([k, r]) => (
                        <tr key={k}>
                          <th>{k}</th>
                          {detailFields.map((f) => (
                            <td key={f}>{fmt(r?.fixtureSettingValues?.[f])}</td>
                          ))}
                          <td>
                            {fmt(r?.played)} / {fmt(r?.home?.played)} /{" "}
                            {fmt(r?.away?.played)}
                          </td>
                          <td>
                            {fmt(
                              (detailScope === "overall" ? r : r?.[detailScope])
                                ?.won
                            )}{" "}
                            /{" "}
                            {fmt(
                              (detailScope === "overall" ? r : r?.[detailScope])
                                ?.draw
                            )}{" "}
                            /{" "}
                            {fmt(
                              (detailScope === "overall" ? r : r?.[detailScope])
                                ?.lost
                            )}
                          </td>
                          <td>
                            {fmt(
                              (detailScope === "overall" ? r : r?.[detailScope])
                                ?.goalsScored
                            )}{" "}
                            :{" "}
                            {fmt(
                              (detailScope === "overall" ? r : r?.[detailScope])
                                ?.goalsAgainst
                            )}
                          </td>
                          <td>
                            {["goalsScored", "goalsAgainst"]
                              .map((f) => {
                                const v =
                                  detailScope === "overall"
                                    ? r
                                    : r?.[detailScope];
                                return v?.played > 0 && v[f] != null
                                  ? fmt(v[f] / v.played)
                                  : "—";
                              })
                              .join(" : ")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <TeamContext
                  key={`${context.season.id}:${t.team_id}`}
                  team={t}
                  seasonId={context.season.id}
                  initialLimit={6}
                />
              </section>
            ))}
          </div>
          <details>
            <summary>{txt("Snapshot / Restore", "Snapshot / Restore")}</summary>
            {revisions.map((r) => (
              <button
                key={r.id}
                disabled={busy || dirty}
                onClick={() =>
                  act(async () => {
                    await request("restore", {
                      restore_id: r.id,
                      confirm: true,
                      run_id: newId(),
                    });
                    setNeedsReload(true);
                    await load(leagueId);
                    setNotice(
                      txt(
                        "Team Settings und Saisonparameter gemeinsam wiederhergestellt.",
                        "Team settings and season parameters restored together."
                      )
                    );
                  })
                }
              >
                {txt("Wiederherstellen", "Restore")} {r.at}
              </button>
            ))}
          </details>
        </>
      )}
    </section>
  );
}
